import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  adminUpdateServiceRequest,
  listAllServiceRequests,
} from "@/lib/partner-services.functions";
import { SERVICE_STATUS_LABELS } from "@/lib/partner-services-catalog";

export const Route = createFileRoute("/_authenticated/admin/service-requests")({
  head: () => ({
    meta: [
      { title: "Service Requests — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminServiceRequests,
});

type Row = {
  id: string;
  client_id: string;
  service_key: string;
  service_label: string;
  status: string;
  partner_note: string | null;
  admin_note: string | null;
  quoted_price_eur: number | null;
  preferred_start: string | null;
  created_at: string;
  crm_clients?: { id: string; name: string | null; company_name: string | null } | null;
};

const STATUSES = [
  "requested",
  "in_review",
  "quoted",
  "approved",
  "in_progress",
  "delivered",
  "declined",
  "cancelled",
] as const;

function AdminServiceRequests() {
  const [filter, setFilter] = useState<string>("");
  const qc = useQueryClient();
  const listFn = useServerFn(listAllServiceRequests);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "service-requests", filter],
    queryFn: () => listFn({ data: filter ? { status: filter } : {} }) as Promise<Row[]>,
    refetchInterval: 8_000,
  });

  const totals = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
    return counts;
  }, [rows]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "service-requests"] });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Partner Service Requests</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review, quote, and deliver upgrade requests from partners.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setFilter("")}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
              filter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"
            }`}
          >
            All ({rows.length})
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
                filter === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              {SERVICE_STATUS_LABELS[s]?.label ?? s} {totals[s] ? `(${totals[s]})` : ""}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}

        <div className="space-y-3">
          {rows.map((r) => (
            <AdminRow key={r.id} row={r} onChanged={invalidate} />
          ))}
          {!isLoading && rows.length === 0 && (
            <p className="text-sm text-slate-500">No requests match this filter.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminRow({ row, onChanged }: { row: Row; onChanged: () => void }) {
  const s = SERVICE_STATUS_LABELS[row.status] ?? { label: row.status, className: "bg-slate-100 text-slate-700" };
  const [status, setStatus] = useState(row.status);
  const [adminNote, setAdminNote] = useState(row.admin_note ?? "");
  const [price, setPrice] = useState(row.quoted_price_eur != null ? String(row.quoted_price_eur) : "");
  const fn = useServerFn(adminUpdateServiceRequest);
  const m = useMutation({
    mutationFn: () =>
      fn({
        data: {
          id: row.id,
          status: status !== row.status ? (status as (typeof STATUSES)[number]) : undefined,
          admin_note: adminNote !== (row.admin_note ?? "") ? adminNote : undefined,
          quoted_price_eur:
            price === ""
              ? row.quoted_price_eur != null
                ? null
                : undefined
              : Number(price) !== row.quoted_price_eur
                ? Number(price)
                : undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Updated");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const clientName = row.crm_clients?.company_name || row.crm_clients?.name || "—";
  const dirty =
    status !== row.status ||
    adminNote !== (row.admin_note ?? "") ||
    (price === "" ? row.quoted_price_eur != null : Number(price) !== row.quoted_price_eur);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-slate-500">{clientName}</div>
          <div className="font-semibold text-slate-900">{row.service_label}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Requested {new Date(row.created_at).toLocaleDateString()}
            {row.preferred_start && ` · prefers start ${new Date(row.preferred_start).toLocaleDateString()}`}
          </div>
          {row.partner_note && (
            <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{row.partner_note}</div>
          )}
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${s.className}`}>
          {s.label}
        </span>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600 mb-1 block">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
          >
            {STATUSES.map((v) => (
              <option key={v} value={v}>
                {SERVICE_STATUS_LABELS[v]?.label ?? v}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600 mb-1 block">Quoted price (€)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
          />
        </label>
        <div className="flex items-end justify-end">
          <button
            onClick={() => m.mutate()}
            disabled={!dirty || m.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {m.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </button>
        </div>
      </div>

      <label className="block mt-3">
        <span className="text-xs font-medium text-slate-600 mb-1 block">Note to partner</span>
        <textarea
          rows={2}
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          placeholder="Shown to the partner alongside the request."
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
        />
      </label>
    </div>
  );
}
